import { NetworkData } from '../types/networkData';
import { gameRedisService } from './gameRedis';
import { swrCache } from './swrCache';

export interface NetworkDataCacheResult {
  data: NetworkData | null;
  key: string;
  /** When the value was read from Redis. */
  cachedAt: number | null;
}

type Stored = { data: NetworkData; key: string };

const KEY = 'redis:network';

/**
 * The playcdu:NetworkData key (1MB+), on the stale-while-revalidate cache.
 *
 * `get()` answers from the cache the moment a copy exists (memory, then IndexedDB) and re-reads
 * Redis in the background; the fresh answer is written back and handed to `onFresh`, so a view
 * paints instantly and then updates. Without a cached copy, or with `forceRefresh`, it waits for
 * Redis. The document is never served from the cache without a re-read.
 */
class NetworkDataCacheService {
  private inflight: Promise<NetworkDataCacheResult> | null = null;

  async get(forceRefresh = false, onFresh?: (fresh: NetworkDataCacheResult) => void): Promise<NetworkDataCacheResult> {
    if (!forceRefresh) {
      const cached = await swrCache.get<Stored>(KEY);
      if (cached) {
        this.revalidate()
          .then((fresh) => onFresh?.(fresh))
          .catch((e) => console.warn('NetworkData re-read failed; showing the cached copy', e));
        return { data: cached.value.data, key: cached.value.key, cachedAt: cached.storedAt };
      }
    }
    return this.revalidate();
  }

  /** One in-flight read at a time; concurrent callers share it. */
  private revalidate(): Promise<NetworkDataCacheResult> {
    if (!this.inflight) {
      this.inflight = (async () => {
        const result = await gameRedisService.getNetworkData();
        if (!result.data) {
          await swrCache.delete(KEY);
          return { data: null, key: result.key, cachedAt: null };
        }
        const entry = await swrCache.set<Stored>(KEY, { data: result.data, key: result.key });
        return { data: result.data, key: result.key, cachedAt: entry.storedAt };
      })().finally(() => {
        this.inflight = null;
      });
    }
    return this.inflight;
  }

  /** When the copy in memory was read from Redis, or null if there is none. */
  getCachedAt(): number | null {
    return swrCache.peek<Stored>(KEY)?.storedAt ?? null;
  }
}

export const networkDataCacheService = new NetworkDataCacheService();
